import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { talleresAPI, paisesAPI } from '../../services/api';
import { Card, CardContent } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Wrench, Search, MapPin, Phone, Mail, Warehouse, ExternalLink } from 'lucide-react';
import { translations } from '../../utils/translations';

// URL del backend - usar URLs relativas en producción
const isLocalDev = typeof window !== 'undefined' && 
  (window.location.hostname === 'localhost' || 
   window.location.hostname.includes('preview.emergentagent.com'));
const BACKEND_URL = isLocalDev ? (process.env.REACT_APP_BACKEND_URL || '') : '';

const TalleresPublicPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { language, t, selectedCountry, setSelectedCountry } = useLanguage();
  const [talleres, setTalleres] = useState({});
  const [paises, setPaises] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  // Textos traducidos
  const txt = translations.talleres;
  const txtHome = translations.home;

  // Click en un taller específico: si tiene sitio_web → nueva pestaña; si no → /login
  const handleTallerClick = (taller) => {
    const url = (taller?.sitio_web || '').trim();
    if (url) {
      const finalUrl = /^https?:\/\//i.test(url) ? url : `https://${url}`;
      window.open(finalUrl, '_blank', 'noopener,noreferrer');
    } else {
      navigate('/login');
    }
  };

  useEffect(() => {
    loadTalleres();
    loadPaises();
  }, []);

  const loadPaises = async () => {
    try {
      const r = await paisesAPI.getPublicos();
      // Mapear al formato esperado por la UI (con comingSoon basado en activo)
      const items = (r.data.items || []).map(p => ({
        code: p.nombre, // se usa como key en talleres[pais.code]
        flagImg: `https://flagcdn.com/w80/${p.codigo.toLowerCase()}.png`,
        name: p.nombre,
        sigla: p.sigla_autoridad,
        comingSoon: !p.activo,
      }));
      setPaises(items);
    } catch (e) {
      console.error('Error cargando países:', e);
      setPaises([]);
    }
  };

  // Resolver la sigla de la autoridad nacional según el país del taller
  const getSiglaPorPais = (paisNombre) => {
    const p = paises.find(x => x.code === paisNombre);
    return p?.sigla || 'ANAC';
  };

  const loadTalleres = async () => {
    try {
      const response = await talleresAPI.getPublicos();
      setTalleres(response.data);
    } catch (error) {
      console.error('Error loading talleres:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredTalleres = (provinciaData) => {
    if (!searchTerm) return provinciaData;
    return provinciaData.filter(t => 
      t.razon_social?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      t.numero_anac?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Header — simétrico con Capacitaciones y Gestoría */}
      <div className="relative bg-gradient-to-r from-teal-600 to-teal-700 text-white py-20 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <div className="bg-white/10 rounded-full p-4 w-fit mx-auto mb-4">
            <Warehouse className="h-12 w-12" />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold mb-3">{t(txt.titulo)}</h1>
          <p className="text-teal-100 text-lg max-w-2xl mx-auto">
            {t(txt.subtitulo)}
          </p>
        </div>
        {/* Gradient transition teal → fondo claro, suaviza el borde inferior */}
        <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-slate-50 to-transparent pointer-events-none" />
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Search */}
        <div className="mb-8">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder={t(txt.buscarPlaceholder)}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>

        {/* Country Tabs */}
        <Tabs value={selectedCountry} onValueChange={setSelectedCountry} className="w-full">
          <TabsList className="flex flex-wrap justify-start gap-2 mb-8 bg-transparent h-auto p-0">
            {paises.map(country => (
              <TabsTrigger 
                key={country.code}
                value={country.code}
                disabled={country.comingSoon}
                className={`
                  flex flex-col items-center gap-1 px-4 py-3 rounded-xl transition-all duration-300
                  ${country.comingSoon 
                    ? 'opacity-40 cursor-not-allowed bg-slate-100' 
                    : 'bg-white shadow-md hover:shadow-lg data-[state=active]:bg-teal-500 data-[state=active]:text-white'
                  }
                `}
              >
                <img src={country.flagImg} alt={country.name} className="w-10 h-7 object-cover rounded shadow-sm" />
                <span className="text-xs font-medium">{country.name}</span>
                {country.comingSoon && <span className="text-[10px] text-slate-400">{t(txtHome.proximamente)}</span>}
              </TabsTrigger>
            ))}
          </TabsList>

          {paises.map(pais => (
            <TabsContent key={pais.code} value={pais.code}>
              {loading ? (
                <div className="text-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-500 mx-auto"></div>
                  <p className="mt-4 text-slate-500">{t(txt.cargando)}</p>
                </div>
              ) : talleres[pais.code] ? (
                <div className="space-y-8">
                  {Object.entries(talleres[pais.code])
                    .sort(([provA], [provB]) => provA.localeCompare(provB, language === 'pt' ? 'pt' : 'es'))
                    .map(([provincia, talleresProv]) => {
                    const filtered = filteredTalleres(talleresProv);
                    if (filtered.length === 0) return null;
                    
                    return (
                      <div key={provincia}>
                        <h2 className="text-lg font-semibold mb-4 text-slate-700 flex items-center gap-2">
                          <MapPin className="h-5 w-5 text-teal-500" />
                          {provincia}
                          <Badge variant="secondary" className="ml-2">{filtered.length}</Badge>
                        </h2>
                        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                          {filtered.map((taller, idx) => (
                            <Card 
                              key={idx} 
                              className="hover:shadow-lg transition-all duration-300 border hover:border-teal-200 cursor-pointer relative group"
                              onClick={() => handleTallerClick(taller)}
                              data-testid={`taller-card-${taller.id || idx}`}
                            >
                              {taller.sitio_web && (
                                <div className="absolute top-2 right-2 bg-teal-500 text-white rounded-full p-1.5 opacity-80 group-hover:opacity-100 transition-opacity" title="Visitar sitio web del taller">
                                  <ExternalLink className="h-3 w-3" />
                                </div>
                              )}
                              <CardContent className="p-5">
                                <div className="flex items-start gap-4">
                                  {taller.logo_url ? (
                                    <img 
                                      src={taller.logo_url.startsWith('http') ? taller.logo_url : `${BACKEND_URL}${taller.logo_url}`} 
                                      alt="" 
                                      className="h-16 w-16 rounded-lg object-contain border bg-white p-1 flex-shrink-0"
                                      onError={(e) => { e.target.style.display = 'none'; }}
                                    />
                                  ) : (
                                    <div className="h-16 w-16 bg-gradient-to-br from-teal-50 to-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                                      <Wrench className="h-8 w-8 text-teal-600" />
                                    </div>
                                  )}
                                  <div className="flex-1 min-w-0">
                                    <h3 className="font-semibold text-slate-800 line-clamp-2 leading-tight">
                                      {taller.razon_social}
                                    </h3>
                                    <Badge className="bg-teal-100 text-teal-700 mt-1.5">
                                      {getSiglaPorPais(pais.code)}: {taller.numero_anac}
                                    </Badge>
                                    
                                    {taller.telefono && (
                                      <p className="text-xs text-slate-500 mt-2 flex items-center gap-1">
                                        <Phone className="h-3 w-3" />
                                        {taller.telefono}
                                      </p>
                                    )}
                                    {taller.email && (
                                      <p className="text-xs text-slate-500 flex items-center gap-1">
                                        <Mail className="h-3 w-3" />
                                        {taller.email}
                                      </p>
                                    )}
                                  </div>
                                </div>
                              </CardContent>
                            </Card>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-12 bg-slate-50 rounded-xl">
                  <Wrench className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500">{t(txt.noHayTalleres)} {pais.name}</p>
                </div>
              )}
            </TabsContent>
          ))}
        </Tabs>
      </div>
    </div>
  );
};

export default TalleresPublicPage;
